import React from 'react';
import {
  Card,
  useClickableCard,
  GDSPosition,
} from '@leagueplatform/genesis-core';
import {
  type MasonryEngineNodeRendererProps,
  type MasonryEngineNode,
} from '@leagueplatform/masonry-engine';
import { MasonryLargeCardContentRenderer } from './masonry-large-card-content-renderer.component';
import type { MasonryLargeCardContentProps } from './masonry-large-card-content-renderer.component';
import { ConditionalLinkCardRenderer } from '../common/conditional-link-card-renderer/conditional-link-card-renderer.component';
import {
  MasonryDriverAction,
  MasonryDismissAction,
} from '../../types/masonry-driver-node-actions';
import { Indicator } from '../../utils/icon-mapping';
import { formatClassNames } from '../../utils/format-masonry-class-name.util';

type MasonryLargeCardNodeProperties = MasonryLargeCardContentProps & {
  image?: string;
  imageAltText?: string;
  imageAlignment?: GDSPosition;
  isChildItem?: boolean;
  showMobile?: Boolean;
  ctaLabel?: string;
  ctaIndicator?: Indicator;
  isCtaLabelledByHeading?: boolean;
};

export type MasonryLargeCardNodeActions = {
  onClick?: MasonryDriverAction;
  onDismiss?: MasonryDismissAction;
};

export type MasonryLargeCardNode = MasonryEngineNode<
  'largeCard',
  MasonryLargeCardNodeProperties,
  MasonryLargeCardNodeActions
>;

export type MasonryLargeCardRendererProps =
  MasonryEngineNodeRendererProps<MasonryLargeCardNode>;

const LARGE_CARD_IMAGE_HEIGHT_FOR_SMALLER_VARIANT = '176px';

export const MasonryLargeCardRenderer = ({
  isChildItem,
  image,
  imageAltText = '',
  imageAlignment,
  onClick,
  showMobile,
  onDismiss,
  ctaIndicator,
  ctaLabel,
  isCtaLabelledByHeading,
  className,
  engineClassName,
  locatorId,
  css,
  rootElementRef,
  ...props
}: MasonryLargeCardRendererProps) => {
  const { primaryActionRef, clickAreaRef, onCardMouseDown, onCardMouseUp } =
    useClickableCard(rootElementRef);

  const showSmallerCard = isChildItem || showMobile;
  const hasCta = Boolean(ctaLabel);
  const hasLink = Boolean(onClick?.payload.url);
  const isConditionalLinkRendererEnabled = hasCta ? false : hasLink;
  const showLeadingImage = image && imageAlignment === 'leading';

  return (
    <ConditionalLinkCardRenderer
      hasLink={isConditionalLinkRendererEnabled}
      ref={clickAreaRef}
      onCardMouseUp={onCardMouseUp}
      onCardMouseDown={onCardMouseDown}
      onDismiss={onDismiss}
      toolbarButton
      hasFloatingDismissButton
      imagePosition={
        showSmallerCard
          ? 'top'
          : { '@initial': showLeadingImage ? 'start' : 'end', '@mobile': 'top' }
      }
      css={{ height: showSmallerCard ? '100%' : 'auto', ...css }}
      className={formatClassNames(engineClassName, className)}
      data-testid={locatorId}
    >
      {image && (
        <Card.Image
          image={image}
          imageAlt={imageAltText}
          imageLoading="lazy"
          css={{
            height: showSmallerCard
              ? LARGE_CARD_IMAGE_HEIGHT_FOR_SMALLER_VARIANT
              : 'auto',
            '@mobile': { height: LARGE_CARD_IMAGE_HEIGHT_FOR_SMALLER_VARIANT },
          }}
        />
      )}
      <Card.Section padding="$one">
        <MasonryLargeCardContentRenderer
          linkRefState={primaryActionRef}
          onClick={onClick}
          ctaLabel={ctaLabel}
          ctaIndicator={ctaIndicator}
          isCtaLabelledByHeading={isCtaLabelledByHeading}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
        />
      </Card.Section>
    </ConditionalLinkCardRenderer>
  );
};
